"use client";
import React, { useEffect, useMemo, useState } from "react";
import { getCourseVideoFrameStyle, normalizeCourseVideoStyle } from "../lib/courseVideoStyle";
import videoFrame from "./courseVideoFrame.module.scss";

const iframeFill = {
    border: 0,
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
};

const CourseVideoPlayer = ({ course, title, fallbackVideoUrl = "", videoStyle: videoStyleProp , autoPlay = false }) => {
    const mode = normalizeCourseVideoStyle(videoStyleProp ?? course?.acf?.video_style);
    const frameStyle = useMemo(() => getCourseVideoFrameStyle(mode), [mode]);
    const frameClass = mode === "portrait" ? videoFrame.portraitHero : "";
    const autoplayClass = autoPlay ? "autoplay" : "";
    const autoplayAttr = autoPlay ? "autoplay" : "";
    const videoSourceType = course.acf?.source; // 'youtube' or 'figmawya'
    const promoVideo = course.acf?.promo;
    const sourceVideo = course.acf?.source_video;
    const [signedBunnyUrl, setSignedBunnyUrl] = useState(null);
    const [bunnySignError, setBunnySignError] = useState("");

    const fallback =
        typeof fallbackVideoUrl === "string" && fallbackVideoUrl.trim() ? fallbackVideoUrl.trim() : "";

    // Primary: promo, then source_video, then optional fallback (e.g. section_intro.episod_1)
    const videoUrl = promoVideo || sourceVideo || fallback || "";

    const isYouTube = useMemo(() => {
        if (!videoUrl) return false;
        return typeof videoUrl === 'string' && (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be") || videoSourceType === 'youtube');
    }, [videoUrl, videoSourceType]);

    // Check if video is an embed URL (mediadelivery, etc.)
    const isEmbedUrl = useMemo(() => {
        if (!videoUrl || typeof videoUrl !== 'string') return false;
        return videoUrl.includes("mediadelivery.net") || 
               videoUrl.includes("iframe.") || 
               videoUrl.includes("/embed/") ||
               videoSourceType === 'figmawya';
    }, [videoUrl, videoSourceType]);

    const isBunnyEmbed = useMemo(() => {
        if (!videoUrl || typeof videoUrl !== 'string') return false;
        return videoUrl.includes('mediadelivery.net') || videoUrl.includes('bunnycdn.com') || videoUrl.includes('.b-cdn.net');
    }, [videoUrl]);

    // Format YouTube URL
    const ytEmbedUrl = useMemo(() => {
        if (!isYouTube || !videoUrl || typeof videoUrl !== 'string') return null;
        let videoId = "";
        if (videoUrl.includes("watch?v=")) {
            videoId = videoUrl.split("watch?v=")[1]?.split("&")[0];
        } else if (videoUrl.includes("youtu.be/")) {
            videoId = videoUrl.split("youtu.be/")[1]?.split("?")[0];
        } else if (videoUrl.includes("embed/")) {
            videoId = videoUrl.split("embed/")[1]?.split("?")[0];
        }

        return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&autoplay=1` : null;
    }, [isYouTube, videoUrl]);

    // Format embed URL with parameters
    const embedUrl = useMemo(() => {
        if (!isEmbedUrl || !videoUrl || typeof videoUrl !== 'string') return null;
        
        // If URL already has query parameters, append to them, otherwise add them
        const separator = videoUrl.includes('?') ? '&' : '?';
        return `${videoUrl}${separator}autoplay=${autoPlay ? 'true' : 'false'}&loop=false&muted=false&preload=true&responsive=true`;
    }, [isEmbedUrl, videoUrl, autoPlay]);

    useEffect(() => {
        let cancelled = false;
        setSignedBunnyUrl(null);
        setBunnySignError("");

        if (!isEmbedUrl || !isBunnyEmbed || !videoUrl || typeof videoUrl !== 'string') {
            return () => {
                cancelled = true;
            };
        }

        const signUrl = async () => {
            try {
                const qs = new URLSearchParams({ url: videoUrl }).toString();
                const res = await fetch(`/api/bunny/sign?${qs}`, { method: 'GET' });
                if (!res.ok) {
                    const payload = await res.json().catch(() => ({}));
                    const message = payload?.message || `Signing failed (${res.status})`;
                    if (!cancelled) {
                        setBunnySignError(message);
                    }
                    return;
                }
                const data = await res.json();
                const signed = typeof data?.embed_url === 'string' ? data.embed_url.trim() : '';
                if (!cancelled && signed) {
                    const separator = signed.includes('?') ? '&' : '?';
                    setSignedBunnyUrl(`${signed}${separator}autoplay=${autoPlay ? 'true' : 'false'}&loop=false&muted=false&preload=true&responsive=true`);
                }
            } catch (err) {
                if (!cancelled) {
                    setBunnySignError(err?.message || 'Signing request failed');
                }
            }
        };

        void signUrl();
        return () => {
            cancelled = true;
        };
    }, [isEmbedUrl, isBunnyEmbed, videoUrl, autoPlay        ]);

    if (!videoUrl) {
        return (
            <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-dark text-white">
                <p>الفيديو غير متوفر حالياً</p>
            </div>
        );
    }

    // YouTube embed
    if (isYouTube && ytEmbedUrl) {
        return (
            <div className={`rounded-4 overflow-hidden ${frameClass}`.trim()} style={frameStyle}>
                <iframe
                    src={ytEmbedUrl}
                    title={title}
                    loading="lazy"
                    style={iframeFill}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
        );
    }

    // Embedded video (mediadelivery, etc.)
    if (isEmbedUrl && embedUrl) {
        if (isBunnyEmbed && !signedBunnyUrl) {
            return (
                <div
                    className={`rounded-4 overflow-hidden d-flex align-items-center justify-content-center bg-dark text-white ${frameClass}`.trim()}
                    style={frameStyle}
                >
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 16,
                            textAlign: "center",
                        }}
                    >
                        <p style={{ margin: 0 }}>
                            {bunnySignError
                                ? `تعذر الوصول إلى رابط فيديو: ${bunnySignError}`
                                : "جاري تجهيز رابط الفيديو ..."}
                        </p>
                    </div>
                </div>
            );
        }
        return (
            <div className={`rounded-4 overflow-hidden ${frameClass}`.trim()} style={frameStyle}>
                <iframe
                    src={isBunnyEmbed ? signedBunnyUrl : embedUrl}
                    title={title}
                    loading="lazy"
                    style={iframeFill}
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                />
            </div>
        );
    }

    // Fallback: show message if URL format is not recognized
    return (
        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-dark text-white">
            <p>صيغة الفيديو غير مدعومة حالياً</p>
        </div>
    );
};

export default CourseVideoPlayer;
